/**
 * Currency types supported by the application (all 15 currencies from provider types)
 */
export type CurrencyCode = 'USD' | 'EUR' | 'GBP' | 'CAD' | 'AUD' | 'INR' | 'MYR' | 'SGD' | 'ZAR' | 'PHP' | 'MXN' | 'LKR' | 'AED' | 'HKD' | 'JPY';

/**
 * Currency configuration for formatting options
 */
export interface CurrencyConfig {
  code: CurrencyCode;
  symbol: string;
  position: 'prefix' | 'suffix';
  decimals: number;
  thousandSeparator: string;
  decimalSeparator: string;
  pdfSymbol?: string; // Alternative symbol for PDF rendering
}

/**
 * Currency configuration for each supported currency
 */
export const CURRENCY_CONFIG: Record<CurrencyCode, CurrencyConfig> = {
  USD: {
    code: 'USD',
    symbol: '$',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  EUR: {
    code: 'EUR',
    symbol: '€',
    position: 'suffix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  GBP: {
    code: 'GBP',
    symbol: '£',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  CAD: {
    code: 'CAD',
    symbol: 'C$',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  AUD: {
    code: 'AUD',
    symbol: 'A$',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  INR: {
    code: 'INR',
    symbol: '₹',
    pdfSymbol: 'Rs.', // Text alternative for PDF
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  MYR: {
    code: 'MYR',
    symbol: 'RM',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  SGD: {
    code: 'SGD',
    symbol: 'S$',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  ZAR: {
    code: 'ZAR',
    symbol: 'R',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  PHP: {
    code: 'PHP',
    symbol: '₱',
    pdfSymbol: 'PHP', // Text alternative for PDF
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  MXN: {
    code: 'MXN',
    symbol: 'MX$',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  LKR: {
    code: 'LKR',
    symbol: 'Rs',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  AED: {
    code: 'AED',
    symbol: 'د.إ',
    pdfSymbol: 'AED', // Text alternative for PDF
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  HKD: {
    code: 'HKD',
    symbol: 'HK$',
    position: 'prefix',
    decimals: 2,
    thousandSeparator: ',',
    decimalSeparator: '.'
  },
  JPY: {
    code: 'JPY',
    symbol: '¥',
    position: 'prefix',
    decimals: 0, // Japanese Yen typically has no decimal places
    thousandSeparator: ',',
    decimalSeparator: '.'
  }
};

/**
 * Format a number as currency according to the specified currency configuration
 * @param value - The number to format
 * @param currencyCode - The currency code (USD, INR, EUR)
 * @param forPDF - Whether this is for PDF rendering (uses alternative symbols when available)
 * @returns Formatted currency string
 */
export function formatCurrency(
  value: number | null | undefined, 
  currencyCode: CurrencyCode = 'USD',
  forPDF: boolean = false
): string {
  // Handle null, undefined, or invalid values
  if (value === null || value === undefined || isNaN(Number(value))) {
    return '-';
  }

  // Ensure we have a valid currency code
  const config = CURRENCY_CONFIG[currencyCode] || CURRENCY_CONFIG.USD;
  
  // Convert to number and handle edge cases
  const numValue = Number(value);
  if (!isFinite(numValue)) {
    return '-';
  }
  
  // Format the number with proper decimals
  const formattedNumber = numValue.toFixed(config.decimals);
  
  // Add thousand separators if needed (for simplicity, basic implementation)
  const parts = formattedNumber.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, config.thousandSeparator);
  const decimalPart = parts.length > 1 ? config.decimalSeparator + parts[1] : '';
  
  const numberWithSeparators = integerPart + decimalPart;
  
  // Use the PDF symbol if available and requested
  const symbolToUse = (forPDF && config.pdfSymbol) ? config.pdfSymbol : config.symbol;
  
  // Ensure we have a valid symbol
  const finalSymbol = symbolToUse || '$';
  
  // Apply symbol according to position
  if (config.position === 'prefix') {
    return `${finalSymbol}${numberWithSeparators}`;
  } else {
    return `${numberWithSeparators} ${finalSymbol}`;
  }
} 